import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Check, ArrowRight, ZoomIn } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import PageShell from "@/components/PageShell";
import SiteFooter from "@/components/SiteFooter";
import { getProductBySlug, getRelated } from "@/data/products";
import { pickImage, pickGallery } from "@/data/imagePool";

export default function ProductDetail() {
   const phoneNumber = "971562973825";
   const defaultMessage =
  "Hello PRINT ZONE! I'd like to get a custom quote for a printing project. Could you please help me with the details and pricing?";
const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(defaultMessage)}`; 
  const { slug } = useParams();
  const product = useMemo(() => {
    const raw = getProductBySlug(slug);
    if (!raw) return null;
    return {
      ...raw,
      image: pickImage(raw.slug, raw.name, 0, true),
      gallery: pickGallery(raw.slug, raw.name, true),
    };
  }, [slug]);

  const related = useMemo(() => {
    if (!product) return [];
    return getRelated(slug).map((p) => ({
      ...p,
      image: pickImage(p.slug, p.name, 0, true),
      gallery: pickGallery(p.slug, p.name, true),
    }));
  }, [slug, product]);
  const [activeImg, setActiveImg] = useState(0);
  const [zoom, setZoom] = useState(false);

  useEffect(() => setActiveImg(0), [slug]);

  if (!product) {
    return (
      <PageShell>
        <div className="pz-container py-32 text-center">
          <h1 className="font-display text-4xl font-bold text-[#0F172A] dark:text-white">Product not found</h1>
          <p className="mt-4 text-slate-600 dark:text-white/70">The product you&apos;re looking for doesn&apos;t exist.</p>
          <Link to="/categories" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full pz-btn-gold text-sm">
            Back to Catalog
          </Link>
        </div>
        <SiteFooter />
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* Breadcrumb */}
      <section className="bg-[#F8FAFC] dark:bg-[#0B1120] border-b border-slate-100 dark:border-white/10">
        <nav data-testid="product-breadcrumb" className="pz-container py-5 text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-white/60 flex flex-wrap items-center gap-2">
          <Link to="/" className="hover:text-[#0F172A] dark:hover:text-white">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/categories" className="hover:text-[#0F172A] dark:hover:text-white">Categories</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#0F172A] dark:text-white">{product.name}</span>
        </nav>
      </section>

      {/* Hero */}
      <section className="bg-white dark:bg-[#0B1120] py-12 md:py-16">
        <div className="pz-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="relative aspect-square rounded-3xl overflow-hidden bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 group"
              data-testid="product-main-image"
            >
              <img
                src={product.gallery[activeImg]}
                alt={product.name}
                className={`w-full h-full object-cover transition-transform duration-500 ${zoom ? "scale-150" : "group-hover:scale-105"}`}
                onMouseEnter={() => setZoom(true)}
                onMouseLeave={() => setZoom(false)}
                onClick={() => setZoom((z) => !z)}
              />
              <button
                onClick={() => setZoom((z) => !z)}
                data-testid="product-zoom-toggle"
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 dark:bg-[#0F172A]/80 backdrop-blur grid place-items-center text-[#0F172A] dark:text-white shadow-md"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </motion.div>

            <div className="mt-4 grid grid-cols-4 gap-3">
              {product.gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  data-testid={`product-thumb-${i}`}
                  className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                    activeImg === i ? "border-[#F59E0B] ring-2 ring-[#F59E0B]/20" : "border-slate-200 dark:border-white/10 hover:border-[#0F172A] dark:hover:border-white/30"
                  }`}
                >
                  <img src={g} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>

          {/* Details */}
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#2563EB] dark:text-[#F59E0B] font-semibold">{product.category}</div>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-[#0F172A] dark:text-white leading-tight">{product.name}</h1>
            <div className="pz-gold-line mt-5" />
            <p className="mt-6 text-slate-600 dark:text-white/70 leading-relaxed">{product.description}</p>

            <div className="mt-7 grid grid-cols-2 gap-3">
              <Link
                to={whatsappUrl}
                data-testid="product-cta-quote"
                className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full pz-btn-gold text-sm"
              >
                Request Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel: 0581568787"
                data-testid="product-cta-call"
                className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full border border-slate-200 dark:border-white/15 text-[#0F172A] dark:text-white text-sm hover:bg-slate-50 dark:hover:bg-white/5"
              >
                Call Now
              </a>
            </div>

            {/* Features */}
            <div className="mt-9">
              <h3 className="font-display text-lg font-semibold text-[#0F172A] dark:text-white">Key Features</h3>
              <ul className="mt-4 space-y-2.5">
                {product.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-slate-700 dark:text-white/75">
                    <Check className="w-4 h-4 mt-0.5 text-[#F59E0B]" strokeWidth={2.5} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            {/* Sizes */}
            <div className="mt-7">
              <h3 className="font-display text-lg font-semibold text-[#0F172A] dark:text-white">Available Sizes</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {product.sizes.map((sz) => (
                  <span key={sz} className="px-3 py-1.5 rounded-full text-xs border border-slate-200 dark:border-white/15 text-slate-700 dark:text-white/80">
                    {sz}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Spec / Materials / Applications */}
      <section className="bg-[#F8FAFC] dark:bg-white/[0.02] py-16 md:py-20 transition-colors">
        <div className="pz-container grid grid-cols-1 lg:grid-cols-3 gap-7">
          <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 p-7">
            <h3 className="font-display text-lg font-semibold text-[#0F172A] dark:text-white">Specifications</h3>
            <dl className="mt-5 space-y-3 text-sm">
              {Object.entries(product.specifications).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-100 dark:border-white/10 pb-3">
                  <dt className="text-slate-500 dark:text-white/55">{k}</dt>
                  <dd className="text-[#0F172A] dark:text-white text-right">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 p-7">
            <h3 className="font-display text-lg font-semibold text-[#0F172A] dark:text-white">Materials</h3>
            <ul className="mt-5 space-y-3 text-sm">
              {product.materials.map((m) => (
                <li key={m} className="flex items-center gap-3 text-slate-700 dark:text-white/75">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" /> {m}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 p-7">
            <h3 className="font-display text-lg font-semibold text-[#0F172A] dark:text-white">Applications</h3>
            <ul className="mt-5 space-y-3 text-sm">
              {product.applications.map((a) => (
                <li key={a} className="flex items-start gap-3 text-slate-700 dark:text-white/75">
                  <Check className="w-4 h-4 mt-0.5 text-[#2563EB] dark:text-[#F59E0B]" strokeWidth={2.5} /> {a}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Product FAQ */}
      <section className="bg-white dark:bg-[#0B1120] py-16 md:py-20">
        <div className="pz-container max-w-3xl">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0F172A] dark:text-white">Frequently Asked</h2>
          <div className="pz-gold-line mt-5" />
          <Accordion type="single" collapsible className="mt-7 space-y-3">
            {product.faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`pfaq-${i}`}
                data-testid={`product-faq-${i}`}
                className="border border-slate-200 dark:border-white/10 rounded-2xl px-6 bg-white dark:bg-white/5"
              >
                <AccordionTrigger className="text-left font-display font-medium text-[#0F172A] dark:text-white hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 dark:text-white/65 text-[15px] leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-[#F8FAFC] dark:bg-white/[0.02] py-16 md:py-20">
          <div className="pz-container">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0F172A] dark:text-white">Related Products</h2>
            <div className="pz-gold-line mt-5" />
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-5">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/categories/${p.slug}`}
                  data-testid={`related-${p.slug}`}
                  className="group rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all"
                >
                  <div className="aspect-square overflow-hidden">
                    <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  </div>
                  <div className="p-4">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#2563EB] dark:text-[#F59E0B] font-semibold">{p.category}</div>
                    <div className="mt-1.5 font-display font-semibold text-sm text-[#0F172A] dark:text-white line-clamp-2">{p.name}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <SiteFooter />
    </PageShell>
  );
}
